import React from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Button, 
  Paper, 
  Stack,
  useMediaQuery,
  useTheme
} from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HomeIcon from '@mui/icons-material/Home';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import ReplayIcon from '@mui/icons-material/Replay';
import { useNavigate } from 'react-router-dom';

const Fail: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleBackToHome = () => {
    navigate('/');
  };

  const handleRetryBooking = () => {
    // Replace with your actual retry booking logic
    navigate('/listings');
  };

  const handleContactSupport = () => {
    // Replace with your actual support contact method
    window.location.href = 'mailto:support@thebatcage.com';
  };

  return (
    <Container maxWidth="sm">
      <Paper 
        elevation={4} 
        sx={{ 
          my: { xs: 4, sm: 8 }, 
          p: { xs: 3, sm: 5 }, 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center',
          borderRadius: 4,
          background: 'linear-gradient(135deg, #fff0f0 0%, #ffe5e5 100%)',
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {/* Subtle background pattern */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: 'radial-gradient(#ffd1d1 8%, transparent 8%)',
            backgroundSize: '30px 30px',
            opacity: 0.3,
            zIndex: 1
          }}
        />

        <Box 
          sx={{ 
            position: 'relative', 
            zIndex: 2, 
            width: '100%' 
          }}
        >
          <ErrorOutlineIcon 
            sx={{ 
              fontSize: isMobile ? 80 : 120, 
              color: 'error.main', 
              mb: 3,
              animation: 'shake 0.5s ease-in-out'
            }} 
          />
          
          <Typography 
            variant={isMobile ? 'h4' : 'h3'} 
            component="h1" 
            gutterBottom 
            sx={{ 
              fontWeight: 700, 
              color: 'text.primary',
              mb: 2,
              letterSpacing: '-0.5px'
            }}
          >
            Booking Failed
          </Typography>
          
          <Typography 
            variant={isMobile ? 'body1' : 'h6'} 
            color="text.secondary" 
            sx={{ 
              mb: 4,
              px: 2,
              maxWidth: 500,
              mx: 'auto'
            }}
          >
            We apologize, but we couldn't complete your booking at this time. 
            There might be a temporary issue with our system or the selected options.
          </Typography>
          
          <Stack 
            spacing={2} 
            direction={isMobile ? 'column' : 'row'}
            sx={{ 
              width: '100%', 
              justifyContent: 'center' 
            }}
          >
            <Button
              variant="contained"
              color="primary"
              startIcon={<ReplayIcon />}
              onClick={handleRetryBooking}
              sx={{
                py: 1.5,
                px: 3,
                borderRadius: 3,
                fontWeight: 600,
                textTransform: 'none',
                boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 8px rgba(0,0,0,0.15)'
                }
              }}
            >
              Try Again
            </Button>

            <Button
              variant="outlined"
              color="secondary"
              startIcon={<SupportAgentIcon />}
              onClick={handleContactSupport}
              sx={{
                py: 1.5,
                px: 3,
                borderRadius: 3,
                fontWeight: 600,
                textTransform: 'none',
                borderWidth: 2,
                '&:hover': {
                  borderWidth: 2,
                  transform: 'translateY(-2px)'
                }
              }}
            >
              Contact Support
            </Button>

            <Button
              variant="text"
              color="primary"
              startIcon={<HomeIcon />}
              onClick={handleBackToHome}
              sx={{
                py: 1.5,
                px: 3,
                borderRadius: 3,
                fontWeight: 600,
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: 'rgba(0,0,0,0.05)'
                }
              }}
            >
              Return Home
            </Button>
          </Stack>
          
          <Typography 
            variant="body2" 
            color="text.secondary" 
            sx={{ 
              mt: 3,
              opacity: 0.7
            }}
          >
            Experiencing difficulties? Our support team is ready to assist you.
          </Typography>
        </Box>
      </Paper>

      {/* Global styles for animation */}
      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          10%, 30%, 50%, 70%, 90% { transform: translateX(-5px); }
          20%, 40%, 60%, 80% { transform: translateX(5px); }
        }
      `}</style>
    </Container>
  );
};

export default Fail;